// The only screen an unauthenticated visitor can reach.
//
// Single user, forever (CLAUDE.md → Boundaries): no sign-up, no magic links, no OAuth buttons, no
// "forgot password" flow that would need an email template nobody has written. One account, one
// form, and a password reset that happens in the Supabase dashboard on the rare day it is needed.
//
// ERRORS ARE SENTENCES. The owner does not read stack traces, and "AuthApiError: Invalid login
// credentials" is a stack trace with better punctuation. Every failure below is translated into
// something that says what happened and what to do about it.

import { useState, type FormEvent } from 'react'

import { getSupabase } from '../lib/auth/supabase'

type Status = 'idle' | 'submitting'

/** Supabase's error strings, turned into something worth reading. */
function plainWords(message: string): string {
  const m = message.toLowerCase()
  if (m.includes('invalid login credentials')) {
    return 'That email and password don’t match. Check the password, then the email.'
  }
  if (m.includes('email not confirmed')) {
    return 'This account exists but its email was never confirmed. Confirm it in the Supabase dashboard.'
  }
  if (m.includes('rate limit') || m.includes('too many')) {
    return 'Too many attempts in a row. Wait a minute and try once more.'
  }
  if (m.includes('failed to fetch') || m.includes('network')) {
    return 'Could not reach the sign-in service. Check the connection, then try again — nothing was lost.'
  }
  return message
}

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const supabase = getSupabase()
    if (!supabase) {
      setError('This build has no Supabase configuration, so there is nothing to sign in to.')
      return
    }

    setStatus('submitting')
    setError(null)
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({ email, password })
      // On success the auth listener in useSession swaps this screen for the app. Nothing to do
      // here — deliberately no navigate() call to race with it.
      if (authError) setError(plainWords(authError.message))
    } catch (thrown) {
      setError(plainWords(thrown instanceof Error ? thrown.message : String(thrown)))
    } finally {
      setStatus('idle')
    }
  }

  return (
    <main className="mx-auto flex min-h-full max-w-sm flex-col justify-center px-6 py-16">
      {/* Same mark as the rail (components/AppShell.tsx) — the owner's own brand mark, not the
          handoff's ink-square "L" this used to duplicate (2026-09-01, see AppShell for why). */}
      <img src="/brand/rail-mark.png" alt="" className="size-[30px]" aria-hidden />
      <h1 className="mt-5 text-section font-semibold">LifeOS</h1>
      <p className="mt-2 text-body-sm text-ink-3">A private workspace. One account — yours.</p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4">
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted">Email</span>
          <input
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-tile border border-line-2 bg-surface px-3 py-2 text-[14px] outline-none focus:border-ink"
          />
        </label>

        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted">Password</span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded-tile border border-line-2 bg-surface px-3 py-2 text-[14px] outline-none focus:border-ink"
          />
        </label>

        {error && (
          <p
            role="alert"
            className="rounded-tile border border-danger-line bg-danger-fill px-3 py-2 text-body-sm text-danger"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full rounded-button bg-ink px-3 py-[10px] text-[13px] font-semibold text-surface transition-opacity duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:opacity-90 disabled:opacity-60 disabled:hover:opacity-60"
        >
          {status === 'submitting' ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p className="mt-8 text-[11px] leading-[1.55] text-microcopy">
        There is no sign-up: this workspace has exactly one owner. Nothing here is shared with
        anyone.
      </p>
    </main>
  )
}
